import { useState, useEffect } from 'react';
import { irApi } from '../lib/api';

const STAGES = [
  { value: 'received', label: 'Received', color: 'bg-gray-100 text-gray-700' },
  { value: 'in_review', label: 'In Review', color: 'bg-blue-100 text-blue-700' },
  { value: 'shortlisted', label: 'Shortlisted', color: 'bg-purple-100 text-purple-700' },
  { value: 'pass', label: 'Pass', color: 'bg-red-100 text-red-600' },
  { value: 'pass_with_interest', label: 'Pass w/ Interest', color: 'bg-yellow-100 text-yellow-700' },
  { value: 'licensing_discussion', label: 'Licensing', color: 'bg-green-100 text-green-700' },
  { value: 'licensed', label: 'Licensed', color: 'bg-emerald-100 text-emerald-700' },
];

const STAGE_MAP = Object.fromEntries(STAGES.map(s => [s.value, s]));

const SOURCES = [
  { value: '', label: 'All Sources' },
  { value: 'portal', label: 'Portal' },
  { value: 'in_person', label: 'In-Person' },
  { value: 'virtual', label: 'Virtual' },
  { value: 'trade_show', label: 'Trade Show' },
];

const CAPABILITY_OPTIONS = ['compound', 'CAD-3D', 'mechanisms', 'card-board', 'app-integration', 'soft-goods', 'art-illustration'];

export default function IRPipeline() {
  const [submissions, setSubmissions] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('kanban'); // kanban or list
  const [filterStage, setFilterStage] = useState('');
  const [filterSource, setFilterSource] = useState('');
  const [search, setSearch] = useState('');

  // Modal states
  const [showNew, setShowNew] = useState(false);
  const [selectedSub, setSelectedSub] = useState(null);
  const [draftingResponse, setDraftingResponse] = useState(false);

  // New submission form
  const [newForm, setNewForm] = useState({
    inventor_name: '', inventor_email: '', idea_summary: '',
    source: 'portal', capability_tags: [], notes: '',
  });
  const [saving, setSaving] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadData(); }, [filterStage, filterSource, search]);

  const loadData = async () => {
    try {
      const params = {};
      if (filterStage) params.stage = filterStage;
      if (filterSource) params.source = filterSource;
      if (search) params.search = search;

      const [subData, statsData] = await Promise.all([
        irApi.list(params),
        irApi.stats(),
      ]);
      setSubmissions(subData.submissions || []);
      setStats(statsData.stats);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newForm.inventor_name || !newForm.idea_summary) return;
    setSaving(true);
    try {
      await irApi.create(newForm);
      setShowNew(false);
      setNewForm({ inventor_name: '', inventor_email: '', idea_summary: '', source: 'portal', capability_tags: [], notes: '' });
      await loadData();
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const handleStageChange = async (id, newStage) => {
    try {
      await irApi.update(id, { pipeline_stage: newStage });
      await loadData();
      if (selectedSub?.id === id) {
        const data = await irApi.get(id);
        setSelectedSub(data.submission);
      }
    } catch (err) { console.error(err); }
  };

  const handleDraftResponse = async (id) => {
    setDraftingResponse(true);
    try {
      await irApi.draftResponse(id);
      const fresh = await irApi.get(id);
      setSelectedSub(fresh.submission);
    } catch (err) { console.error(err); }
    setDraftingResponse(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this submission?')) return;
    try {
      await irApi.delete(id);
      setSelectedSub(null);
      await loadData();
    } catch (err) { console.error(err); }
  };

  // Kanban columns
  const kanbanStages = ['received', 'in_review', 'shortlisted', 'licensing_discussion', 'licensed'];

  // Detail view
  if (selectedSub) {
    const stage = STAGE_MAP[selectedSub.pipeline_stage];
    return (
      <div>
        <button onClick={() => setSelectedSub(null)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-vault-navy mb-4 transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Pipeline
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-xl font-bold text-vault-navy">{selectedSub.inventor_name}</h1>
                  {selectedSub.inventor_email && <p className="text-sm text-gray-400">{selectedSub.inventor_email}</p>}
                </div>
                <span className={`px-3 py-1 rounded-full text-xs font-semibold ${stage?.color}`}>{stage?.label}</span>
              </div>

              <div className="mb-4">
                <div className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">Idea Summary</div>
                <p className="text-gray-700">{selectedSub.idea_summary}</p>
              </div>

              <div className="grid grid-cols-2 gap-4 mb-4">
                <div>
                  <div className="text-xs text-gray-400">Source</div>
                  <div className="text-sm text-vault-navy capitalize">{selectedSub.source?.replace(/_/g, ' ')}</div>
                </div>
                <div>
                  <div className="text-xs text-gray-400">Submitted</div>
                  <div className="text-sm text-vault-navy">{new Date(selectedSub.submission_date).toLocaleDateString()}</div>
                </div>
              </div>

              {selectedSub.capability_tags?.length > 0 && (
                <div className="mb-4">
                  <div className="text-xs text-gray-400 mb-1">Capabilities</div>
                  <div className="flex flex-wrap gap-1">
                    {selectedSub.capability_tags.map(t => (
                      <span key={t} className="text-xs px-2 py-0.5 bg-vault-surface rounded-full text-gray-600">{t}</span>
                    ))}
                  </div>
                </div>
              )}

              {selectedSub.notes && (
                <div className="mb-4">
                  <div className="text-xs text-gray-400 mb-1">Notes</div>
                  <p className="text-sm text-gray-600 bg-vault-surface rounded-lg p-3">{selectedSub.notes}</p>
                </div>
              )}

              <div className="flex gap-2">
                <button onClick={() => handleDelete(selectedSub.id)}
                  className="px-3 py-1.5 border border-vault-error/30 text-vault-error text-sm rounded-lg hover:bg-red-50 transition-colors">
                  Delete
                </button>
              </div>
            </div>

            {/* AI Draft Response */}
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-vault-navy">AI Draft Response</h2>
                <button onClick={() => handleDraftResponse(selectedSub.id)}
                  disabled={draftingResponse}
                  className="px-3 py-1.5 bg-vault-navy hover:bg-vault-slate text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                  {draftingResponse ? 'Drafting...' : selectedSub.ai_draft_response ? 'Re-draft' : 'Generate Draft'}
                </button>
              </div>
              {selectedSub.ai_draft_response ? (
                <div className="p-4 bg-vault-surface rounded-lg">
                  <p className="text-sm text-gray-700 whitespace-pre-wrap">{selectedSub.ai_draft_response}</p>
                  <p className="text-xs text-gray-400 mt-3 italic">Review and edit before sending. AI never sends automatically.</p>
                </div>
              ) : (
                <p className="text-sm text-gray-400">No draft response yet. Click "Generate Draft" to create one based on the current pipeline stage.</p>
              )}
            </div>
          </div>

          {/* Stage changer sidebar */}
          <div>
            <div className="bg-white rounded-xl border border-vault-border p-6">
              <h2 className="text-lg font-semibold text-vault-navy mb-4">Move Stage</h2>
              <div className="space-y-2">
                {STAGES.map(s => (
                  <button key={s.value}
                    onClick={() => handleStageChange(selectedSub.id, s.value)}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                      selectedSub.pipeline_stage === s.value
                        ? `${s.color} ring-2 ring-offset-1 ring-vault-gold`
                        : 'bg-vault-surface text-gray-600 hover:bg-gray-200'
                    }`}>
                    {s.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;

  return (
    <div>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-vault-navy">IR Pipeline</h1>
          <p className="text-gray-500 text-sm mt-1">{stats?.total || 0} total submissions</p>
        </div>
        <div className="flex gap-2">
          <div className="flex bg-vault-surface rounded-lg p-1">
            <button onClick={() => setView('kanban')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${view === 'kanban' ? 'bg-white shadow-sm text-vault-navy' : 'text-gray-500'}`}>
              Kanban
            </button>
            <button onClick={() => setView('list')}
              className={`px-3 py-1.5 rounded-md text-sm font-medium ${view === 'list' ? 'bg-white shadow-sm text-vault-navy' : 'text-gray-500'}`}>
              List
            </button>
          </div>
          <button onClick={() => setShowNew(true)}
            className="px-4 py-2 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg transition-colors">
            + Add Submission
          </button>
        </div>
      </div>

      {/* Stale warning */}
      {stats?.stale_reviews > 0 && (
        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-2">
          <span className="text-yellow-600 font-medium text-sm">
            {stats.stale_reviews} submission{stats.stale_reviews !== 1 ? 's have' : ' has'} been in review for over 14 days
          </span>
        </div>
      )}

      {/* Filters (list view) */}
      {view === 'list' && (
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <input type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search inventors or ideas..."
            className="flex-1 px-3 py-2 border border-vault-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-vault-gold" />
          <select value={filterStage} onChange={e => setFilterStage(e.target.value)}
            className="px-3 py-2 border border-vault-border rounded-lg text-sm bg-white">
            <option value="">All Stages</option>
            {STAGES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
          <select value={filterSource} onChange={e => setFilterSource(e.target.value)}
            className="px-3 py-2 border border-vault-border rounded-lg text-sm bg-white">
            {SOURCES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
      )}

      {/* Kanban View */}
      {view === 'kanban' ? (
        <div className="flex gap-4 overflow-x-auto pb-4">
          {kanbanStages.map(stageKey => {
            const stage = STAGE_MAP[stageKey];
            const items = submissions.filter(s => s.pipeline_stage === stageKey);
            return (
              <div key={stageKey} className="min-w-[260px] flex-shrink-0">
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${stage.color}`}>{stage.label}</span>
                  <span className="text-xs text-gray-400">{items.length}</span>
                </div>
                <div className="space-y-2">
                  {items.map(sub => (
                    <button key={sub.id} onClick={() => setSelectedSub(sub)}
                      className="w-full text-left bg-white rounded-lg border border-vault-border p-3 hover:shadow-md transition-shadow">
                      <div className="font-medium text-vault-navy text-sm">{sub.inventor_name}</div>
                      <p className="text-xs text-gray-500 line-clamp-2 mt-1">{sub.idea_summary}</p>
                      <div className="flex items-center gap-2 mt-2">
                        <span className="text-xs text-gray-400 capitalize">{sub.source?.replace(/_/g, ' ')}</span>
                        <span className="text-xs text-gray-300">|</span>
                        <span className="text-xs text-gray-400">{new Date(sub.submission_date).toLocaleDateString()}</span>
                      </div>
                      {sub.capability_tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {sub.capability_tags.slice(0, 3).map(t => (
                            <span key={t} className="text-xs px-1.5 py-0.5 bg-vault-surface rounded text-gray-500">{t}</span>
                          ))}
                        </div>
                      )}
                    </button>
                  ))}
                  {items.length === 0 && (
                    <div className="text-center py-6 text-gray-300 text-xs">Empty</div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* List View */
        <div className="bg-white rounded-xl border border-vault-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-vault-border bg-vault-surface">
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Inventor</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Idea</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Stage</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="text-left py-3 px-4 text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody>
              {submissions.map(sub => {
                const stage = STAGE_MAP[sub.pipeline_stage];
                return (
                  <tr key={sub.id} onClick={() => setSelectedSub(sub)}
                    className="border-b border-vault-border hover:bg-gray-50 cursor-pointer">
                    <td className="py-3 px-4 font-medium text-vault-navy">{sub.inventor_name}</td>
                    <td className="py-3 px-4 text-gray-600 max-w-xs truncate">{sub.idea_summary}</td>
                    <td className="py-3 px-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${stage?.color}`}>{stage?.label}</span>
                    </td>
                    <td className="py-3 px-4 text-gray-500 capitalize">{sub.source?.replace(/_/g, ' ')}</td>
                    <td className="py-3 px-4 text-gray-500">{new Date(sub.submission_date).toLocaleDateString()}</td>
                  </tr>
                );
              })}
              {submissions.length === 0 && (
                <tr><td colSpan={5} className="py-8 text-center text-gray-400">No submissions found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* New Submission Modal */}
      {showNew && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-vault-navy">New Submission</h2>
                <button onClick={() => setShowNew(false)} className="text-gray-400 hover:text-gray-600 text-2xl">&times;</button>
              </div>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inventor Name *</label>
                  <input type="text" value={newForm.inventor_name}
                    onChange={e => setNewForm({ ...newForm, inventor_name: e.target.value })}
                    className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inventor Email</label>
                  <input type="email" value={newForm.inventor_email}
                    onChange={e => setNewForm({ ...newForm, inventor_email: e.target.value })}
                    className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Idea Summary *</label>
                  <textarea value={newForm.idea_summary} rows={4}
                    onChange={e => setNewForm({ ...newForm, idea_summary: e.target.value })}
                    placeholder="IR rep's notes about the submitted idea..."
                    className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                  <select value={newForm.source} onChange={e => setNewForm({ ...newForm, source: e.target.value })}
                    className="w-full px-3 py-2 border border-vault-border rounded-lg bg-white">
                    {SOURCES.filter(s => s.value).map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Inventor Capabilities</label>
                  <div className="flex flex-wrap gap-2">
                    {CAPABILITY_OPTIONS.map(cap => (
                      <label key={cap} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm cursor-pointer border transition-colors ${
                        newForm.capability_tags.includes(cap) ? 'border-vault-gold bg-yellow-50 text-vault-gold-dark' : 'border-vault-border text-gray-600 hover:border-gray-300'
                      }`}>
                        <input type="checkbox" checked={newForm.capability_tags.includes(cap)}
                          onChange={e => {
                            const tags = e.target.checked
                              ? [...newForm.capability_tags, cap]
                              : newForm.capability_tags.filter(t => t !== cap);
                            setNewForm({ ...newForm, capability_tags: tags });
                          }}
                          className="hidden" />
                        {cap}
                      </label>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea value={newForm.notes} rows={2}
                    onChange={e => setNewForm({ ...newForm, notes: e.target.value })}
                    className="w-full px-3 py-2 border border-vault-border rounded-lg focus:outline-none focus:ring-2 focus:ring-vault-gold resize-none" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button type="button" onClick={() => setShowNew(false)}
                    className="flex-1 py-2.5 border border-vault-border text-gray-600 font-medium rounded-lg hover:bg-gray-50">Cancel</button>
                  <button type="submit" disabled={saving}
                    className="flex-1 py-2.5 bg-vault-gold hover:bg-vault-gold-dark text-white font-medium rounded-lg disabled:opacity-50">
                    {saving ? 'Saving...' : 'Add Submission'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
